import { Box, Drawer, DrawerProps, useTheme } from '@mui/material';
import { jobDrawerWidth } from 'components/atoms/FlexContainers/FlexContainers';
import { SITE_HEADER_HEIGHT } from 'components/SiteHeader/SiteHeader';
import { Job, JobStatus } from 'interfaces/job';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useJob } from 'utils/queryHooks/jobs/useJob';
import { JobDetails } from './JobDetails';
import { JobDrawerResizeHandle } from './JobDrawerResizeHandle';
import { useJobDrawerResizer, useJobDrawerWidth } from './jobDrawerSizing';

const ACTIVE_JOB_REFRESH_MS = 10_000;

const isJobActive = (job?: Job) => job?.status === JobStatus.Pending || job?.status === JobStatus.Running;

interface SharedJobDetailsProps {
  currentJobId: string;
  prefetchedJobInfo?: Partial<Job>;
  skipQuery?: boolean;
  onClose?: () => void;
  onJobLoaded?: (job: Job) => void;
}

const useJobDrawerData = ({
  currentJobId,
  prefetchedJobInfo,
  jobDrawerOpen,
  skipQuery,
}: SharedJobDetailsProps & { jobDrawerOpen: boolean }) => {
  return useJob<Job>(currentJobId, {
    refetchOnMount: 'always',
    enabled: !isEmpty(currentJobId) && jobDrawerOpen && !skipQuery,
    placeholderData: prefetchedJobInfo as Job,
    // Keep the step history and progress current while the job is still pending or running.
    refetchInterval: (job) => (isJobActive(job) ? ACTIVE_JOB_REFRESH_MS : false),
  });
};

export const EmbeddedJobDetailsPanel: React.FC<SharedJobDetailsProps & { resizable?: boolean }> = ({
  currentJobId,
  prefetchedJobInfo,
  skipQuery,
  onClose,
  onJobLoaded,
  resizable = false,
}) => {
  const theme = useTheme();
  const { jobDrawerWidth: persistedDrawerWidth, setJobDrawerWidth } = useJobDrawerWidth();
  const { isResizingDrawer, minDrawerWidth, maxDrawerWidth, handleResizePointerDown, handleResizeHandleKeyDown } =
    useJobDrawerResizer({ drawerWidth: persistedDrawerWidth, edge: 'left', setJobDrawerWidth });
  const panelRef = useRef<HTMLDivElement>(null);
  const {
    data: job,
    isLoading,
    isSuccess,
    isPlaceholderData,
    isFetchedAfterMount,
  } = useJobDrawerData({
    currentJobId,
    prefetchedJobInfo,
    skipQuery,
    jobDrawerOpen: !isEmpty(currentJobId),
    onClose,
  });

  // Cached or placeholder data may be stale (e.g. the job was archived elsewhere); only a response fetched by this panel is authoritative.
  const isAuthoritativeJob = isSuccess && isFetchedAfterMount && !isPlaceholderData;

  useEffect(() => {
    if (job?.id && isAuthoritativeJob) {
      onJobLoaded?.(job);
    }
  }, [job, isAuthoritativeJob, onJobLoaded]);

  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.scrollTop = 0;
    }
  }, [currentJobId]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: resizable ? persistedDrawerWidth : jobDrawerWidth,
        height: '100%',
        flexShrink: 0,
      }}
    >
      <Box
        ref={panelRef}
        aria-label="Job details"
        sx={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <JobDetails
          job={job}
          isLoading={isLoading}
          onJobArchived={onClose}
          onClosed={
            onClose
              ? (event) => {
                  event.stopPropagation();
                  onClose();
                }
              : undefined
          }
        />
      </Box>
      {resizable && (
        <JobDrawerResizeHandle
          edge="left"
          width={persistedDrawerWidth}
          minWidth={minDrawerWidth}
          maxWidth={maxDrawerWidth}
          isResizing={isResizingDrawer}
          onPointerDown={handleResizePointerDown}
          onKeyDown={handleResizeHandleKeyDown}
        />
      )}
    </Box>
  );
};

export const JobDrawer: React.FC<
  React.PropsWithChildren<{
    headerHeight?: number | string;
    jobDrawerOpen: boolean;
    setJobDrawerOpen: (open: boolean) => void;
    currentJobId: string;
    prefetchedJobInfo?: Partial<Job>;
    skipQuery?: boolean;
    anchor?: 'right' | 'left' | 'top' | 'bottom';
    sx?: DrawerProps['sx'];
  }>
> = ({
  prefetchedJobInfo,
  jobDrawerOpen,
  setJobDrawerOpen,
  currentJobId,
  headerHeight = SITE_HEADER_HEIGHT,
  skipQuery,
  anchor = 'right',
  sx,
}) => {
  const theme = useTheme();
  const { jobDrawerWidth: drawerWidth, setJobDrawerWidth } = useJobDrawerWidth();
  const resizeEdge = anchor === 'left' ? 'right' : 'left';
  const { isResizingDrawer, minDrawerWidth, maxDrawerWidth, handleResizePointerDown, handleResizeHandleKeyDown } =
    useJobDrawerResizer({ drawerWidth, edge: resizeEdge, setJobDrawerWidth });

  const { data: job, isLoading } = useJobDrawerData({
    currentJobId,
    prefetchedJobInfo,
    skipQuery,
    jobDrawerOpen,
  });

  return (
    <Drawer
      key={job?.id}
      anchor={anchor}
      open={jobDrawerOpen}
      onClick={(event) => event.stopPropagation()}
      onClose={() => setJobDrawerOpen(false)}
      variant={'persistent'}
      ModalProps={{ keepMounted: false }}
      sx={{
        width: drawerWidth,
        ...sx,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          zIndex: theme.zIndex.appBar - 1,
          overflow: 'hidden',
          height: `calc(100% - ${headerHeight})`,
          marginTop: headerHeight,
          transition: isResizingDrawer
            ? 'none'
            : theme.transitions.create('width', {
                duration: theme.transitions.duration.shorter,
                easing: theme.transitions.easing.easeOut,
              }),
          ...((sx as any)?.['& .MuiDrawer-paper'] || {}),
        },
      }}
    >
      {/* the keepMounted = false is having issues in react 18 therefore we use this boolean check to prevent rendering unneeded components */}
      {jobDrawerOpen && (
        <Box sx={{ position: 'relative', display: 'flex', height: '100%', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, minWidth: 0, height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
            <JobDetails
              job={job}
              isLoading={isLoading}
              onJobArchived={() => setJobDrawerOpen(false)}
              onClosed={(event) => {
                event.stopPropagation();
                setJobDrawerOpen(false);
              }}
            />
          </Box>
          <JobDrawerResizeHandle
            edge={resizeEdge}
            width={drawerWidth}
            minWidth={minDrawerWidth}
            maxWidth={maxDrawerWidth}
            isResizing={isResizingDrawer}
            onPointerDown={handleResizePointerDown}
            onKeyDown={handleResizeHandleKeyDown}
          />
        </Box>
      )}
    </Drawer>
  );
};
